import { keyframes } from '@emotion/react';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CelebrationRoundedIcon from '@mui/icons-material/CelebrationRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import RateReviewRoundedIcon from '@mui/icons-material/RateReviewRounded';
import { Box, Button, IconButton, LinearProgress, Stack, Typography } from '@mui/material';
import React from 'react';
import { Redirect, useHistory, useParams } from 'react-router-dom';
import PhoneFrame from '../components/PhoneFrame';
import ChooseNextExerciseCard from '../components/exercises/ChooseNextExerciseCard';
import ConversationExerciseCard from '../components/exercises/ConversationExerciseCard';
import ImageExerciseCard from '../components/exercises/ImageExerciseCard';
import ImageSelectSpeakExerciseCard from '../components/exercises/ImageSelectSpeakExerciseCard';
import ListenRepeatExerciseCard from '../components/exercises/ListenRepeatExerciseCard';
import MatchGameExerciseCard from '../components/exercises/MatchGameExerciseCard';
import QuizExerciseCard from '../components/exercises/QuizExerciseCard';
import QuizWordExerciseCard from '../components/exercises/QuizWordExerciseCard';
import SpeakingExerciseCard from '../components/exercises/SpeakingExerciseCard';
import SummaryExerciseCard from '../components/exercises/SummaryExerciseCard';
import TypeSpeakExerciseCard from '../components/exercises/TypeSpeakExerciseCard';
import WordIntroExerciseCard from '../components/exercises/WordIntroExerciseCard';
import { markExerciseComplete, mockCourses } from '../mockData';
import { getLessonExercises, LessonExercise } from '../mockLessonContent';
import { BLUE, GREEN, INTER, NAVY, POPPINS, RING_TRACK, SLATE, SURFACE_SOFT, yellowButtonSx } from '../studentStyles';

const popIn = keyframes`
    from { transform: scale(0.6); opacity: 0; }
    to { transform: scale(1); opacity: 1; }
`;

// Steps with nothing to answer — a picture, a new word, a conversation the
// student only listens to, the closing summary — are ready to continue right
// away. Everything else waits for the answer, recording or match.
function defaultReadyFor(exercise: LessonExercise | undefined): boolean {
    if (exercise == null) {
        return true;
    }
    if (exercise.kind === 'conversation') {
        return exercise.mode === 'listen' || exercise.mode === 'listenNoCharacters';
    }
    return exercise.kind === 'word' || exercise.kind === 'image' || exercise.kind === 'summary';
}

function renderExercise(exercise: LessonExercise, onReady: () => void) {
    switch (exercise.kind) {
        case 'image':
            return <ImageExerciseCard exercise={exercise} />;
        case 'word':
            return <WordIntroExerciseCard exercise={exercise} />;
        case 'listenRepeat':
            return <ListenRepeatExerciseCard exercise={exercise} onReady={onReady} />;
        case 'quiz':
            return <QuizExerciseCard exercise={exercise} onReady={onReady} />;
        case 'quizWord':
            return <QuizWordExerciseCard exercise={exercise} onReady={onReady} />;
        case 'match':
            return <MatchGameExerciseCard exercise={exercise} onReady={onReady} />;
        case 'imageSelectSpeak':
            return <ImageSelectSpeakExerciseCard exercise={exercise} onReady={onReady} />;
        case 'typeSpeak':
            return <TypeSpeakExerciseCard exercise={exercise} onReady={onReady} />;
        case 'speak':
            return <SpeakingExerciseCard exercise={exercise} onReady={onReady} />;
        case 'conversation':
            return <ConversationExerciseCard exercise={exercise} onReady={onReady} />;
        case 'summary':
            return <SummaryExerciseCard exercise={exercise} />;
        case 'chooseNext':
            return <ChooseNextExerciseCard exercise={exercise} onReady={onReady} />;
    }
}

// Full-screen step player: one exercise per screen, resumed from the first
// incomplete prompt. Visual style adapted from the teacher mockup's
// StudentActivityPreview, re-tokenized to the student palette.
export default function StudentLessonPlayer() {
    const history = useHistory();
    const { courseId, lessonId } = useParams() as { courseId: string; lessonId: string };

    const course = mockCourses.find(c => c.id === Number(courseId));
    const lesson = course?.lessons.find(l => l.id === Number(lessonId));

    const exercises = React.useMemo(() => (lesson ? getLessonExercises(lesson) : []), [lesson]);
    // Resume at the first incomplete exercise; a finished lesson replays from the start.
    const initialIndex = !lesson || lesson.completedPrompts >= lesson.totalPrompts ? 0 : lesson.completedPrompts;
    const [index, setIndex] = React.useState(initialIndex);
    const [ready, setReady] = React.useState(() => defaultReadyFor(exercises[initialIndex]));

    if (!course) {
        return <Redirect to="/mockup/student" />;
    }
    if (!lesson) {
        return <Redirect to={`/mockup/student/course/${course.id}`} />;
    }

    const total = exercises.length;
    const finished = index >= total;
    const exercise = exercises[index];
    const coursePath = `/mockup/student/course/${course.id}`;
    // "Start next lesson" — the finish screen stands in for DialogPromptStartNextLessonModel.
    const lessonIndex = course.lessons.findIndex(l => l.id === lesson.id);
    const nextLesson = course.lessons[lessonIndex + 1];

    const handleContinue = () => {
        markExerciseComplete(lesson, index);
        const next = index + 1;
        setIndex(next);
        setReady(defaultReadyFor(exercises[next]));
    };

    return (
        <PhoneFrame>
            <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 96px)' }}>

                {/* Top bar: exit + lesson progress */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                    <IconButton aria-label="Exit lesson" onClick={() => history.push(coursePath)} sx={{ ml: -1, color: SLATE }}>
                        <CloseRoundedIcon />
                    </IconButton>
                    <LinearProgress
                        variant="determinate"
                        value={Math.min(100, (index / total) * 100)}
                        sx={{
                            flex: 1,
                            height: 8,
                            borderRadius: '999px',
                            backgroundColor: RING_TRACK,
                            '& .MuiLinearProgress-bar': { borderRadius: '999px', backgroundColor: BLUE },
                        }}
                    />
                    <Typography sx={{ fontFamily: INTER, fontWeight: 600, fontSize: 13, color: SLATE, whiteSpace: 'nowrap' }}>
                        {Math.min(index + 1, total)} of {total}
                    </Typography>
                </Box>

                {finished ? (
                    <>
                        <Stack alignItems="center" justifyContent="center" sx={{ flex: 1, textAlign: 'center', py: 4 }}>
                            <Box sx={{
                                width: 96, height: 96, borderRadius: '50%', backgroundColor: `${GREEN}1A`,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                animation: `${popIn} 300ms ease-out`,
                            }}>
                                <CelebrationRoundedIcon sx={{ color: GREEN, fontSize: 48 }} />
                            </Box>
                            <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 24, color: NAVY, mt: 2.5 }}>
                                Lesson complete!
                            </Typography>
                            <Typography sx={{ fontFamily: INTER, fontSize: 14, color: SLATE, mt: 0.75 }}>
                                You finished {lesson.name}
                            </Typography>
                            <Box sx={{ mt: 3, width: '100%', p: 2, borderRadius: '16px', backgroundColor: SURFACE_SOFT, textAlign: 'center' }}>
                                <CheckCircleRoundedIcon sx={{ color: GREEN, fontSize: 24 }} />
                                <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 16, color: NAVY, mt: 0.5 }}>
                                    {total} exercises
                                </Typography>
                                <Typography sx={{ fontFamily: INTER, fontSize: 12, color: SLATE }}>
                                    all done
                                </Typography>
                            </Box>
                        </Stack>
                        <Stack spacing={1} sx={{ mt: 3 }}>
                            {nextLesson ? (
                                <>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        onClick={() => history.push(`${coursePath}/lesson/${nextLesson.id}`)}
                                        sx={{ ...yellowButtonSx, py: 1.5 }}
                                    >
                                        Start the next lesson
                                    </Button>
                                    <Button fullWidth onClick={() => history.push(coursePath)} sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 14, color: BLUE }}>
                                        Back to course
                                    </Button>
                                </>
                            ) : (
                                <>
                                    <Button fullWidth variant="contained" onClick={() => history.push(coursePath)} sx={{ ...yellowButtonSx, py: 1.5 }}>
                                        Back to course
                                    </Button>
                                    <Button fullWidth onClick={() => history.push('/mockup/student')} sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 14, color: BLUE }}>
                                        Home
                                    </Button>
                                </>
                            )}
                            <Button
                                fullWidth
                                startIcon={<RateReviewRoundedIcon />}
                                onClick={() => history.push('/mockup/student/feedback')}
                                sx={{ fontFamily: INTER, fontWeight: 600, fontSize: 13, color: SLATE }}
                            >
                                See your teacher&apos;s feedback
                            </Button>
                        </Stack>
                    </>
                ) : (
                    <>
                        <Box key={exercise.id} sx={{ flex: 1 }}>
                            {renderExercise(exercise, () => setReady(true))}
                        </Box>
                        <Button
                            fullWidth
                            variant="contained"
                            disabled={!ready}
                            endIcon={<ArrowForwardRoundedIcon />}
                            onClick={handleContinue}
                            sx={{ ...yellowButtonSx, py: 1.5, mt: 3 }}
                        >
                            {index === total - 1 ? 'Finish lesson' : 'Continue'}
                        </Button>
                    </>
                )}
            </Box>
        </PhoneFrame>
    );
}
